import { yupResolver } from '@hookform/resolvers/yup';
import { CompanyInformationForm } from '@src/clients/CompanyInformationForm';
import { useClientCreation } from '@src/clients/hooks/useClientCreation';
import { AdminClientFormSchema } from '@src/clients/schemas/AdminClientFormSchema';
import type { ClientFormSchema } from '@src/clients/schemas/ClientFormSchema';
import { CancelButton } from '@src/common/components/button/CancelButton';
import { CreateButton } from '@src/common/components/button/CreateButton';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { useEffect, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { type Control, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

interface Props<T extends FieldValues> {
  agencyId?: string;
  AdditionalDetails?: (props: { control: Control<T> }) => JSX.Element;
  onAddClientSuccess?: () => void;
  onCancel?: () => void;
}

export const AddClient = ({
  agencyId,
  AdditionalDetails,
  onAddClientSuccess,
  onCancel,
}: Props<yup.InferType<typeof ClientFormSchema>>) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [mailingIsSameAsPhysical, setMailingIsSameAsPhysical] = useState(true);
  const { createClient, isLoading } = useClientCreation({
    onSuccess: () => {
      onAddClientSuccess?.();
      showToast({
        severity: ToastLevel.success,
        message: t('changesSaved'),
      });
    },
  });

  const { control, unregister, handleSubmit } = useForm({
    resolver: yupResolver(AdminClientFormSchema),
    context: {
      isMailingAddressSameAsPhysical: mailingIsSameAsPhysical,
    },
  });

  useEffect(() => {
    if (mailingIsSameAsPhysical) {
      unregister('mailingAddress', { keepDefaultValue: false });
    }
  }, [unregister, mailingIsSameAsPhysical]);

  const submitClient: SubmitHandler<
    yup.InferType<typeof AdminClientFormSchema>
  > = (data) => {
    createClient({ agencyId, data });
  };
  return (
    <PageLayout title={t('addClient')}>
      <form onSubmit={handleSubmit(submitClient)}>
        <Stack direction="column" gap={theme.spacing['9']}>
          <CompanyInformationForm
            control={control}
            onMailingIsSameAsPhysicalChange={setMailingIsSameAsPhysical}
            mailingIsSameAsPhysical={mailingIsSameAsPhysical}
            AdditionalDetails={AdditionalDetails}
          />
          <Stack
            marginTop={theme.spacing['6']}
            direction="row"
            justifyContent="right"
            gap={2}
          >
            <CancelButton onClick={onCancel} />

            <CreateButton
              disabled={isLoading}
              onClick={handleSubmit(submitClient)}
            />
          </Stack>
        </Stack>
      </form>
    </PageLayout>
  );
};
